import styled from '@emotion/styled';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { Color } from '@diritto/common/style/color';
import { ContentCover } from 'ui-components/contents/ContentCover';
import { DotDivider } from 'ui-components/common/DotDivider';
import { ContentBodyPaddingContainer } from 'ui-components/layout/containers/ContentBodyCenterContainer';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import React, { MouseEvent, useCallback } from 'react';
import {
  ClickAction,
  ClickTargetType,
  TrackerExtraInfo,
  TrackerPosition,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import ContentFavoriteAndEpisodeNotiButton from 'app-components/contents/ContentFavoriteAndEpisodeNotiButton';
import { getTextLineStyle } from 'styles/common';
import { useRouter } from 'next/navigation';
import { useTrackingState } from 'contexts/TrackingProvider';
import { ProfileName } from 'app-components/contents/ProfileName';

function stopPropagation(e: MouseEvent) {
  e.stopPropagation();
}

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.RollingViewer,
};

export const RollingViewerContentBanner = ({
  content,
  profile,
  className,
}: {
  content: ContentPublicRelayDto;
  profile?: ProfileLitePublicRelayDto;
  className?: string;
}) => {
  const router = useRouter();
  const { tracker } = useTrackingState();

  const handleContentBannerClick = useCallback(() => {
    tracker.track(TrackingEventType.Click, {
      clickType: ClickTargetType.Banner,
      action: ClickAction.ToContentDetail,
      trackerExtraInfo,
    });
    return router.push(`/contents/${content.id}`);
  }, [content.id, router, tracker]);

  return (
    <StyledContentBannerWrapper className={className} onClick={handleContentBannerClick}>
      <StyledContentBanner>
        <StyledNewContentCover content={content} size={40} />
        <StyledContentMetaWrapper>
          <StyledContentMetaTitle>{content.title}</StyledContentMetaTitle>
          <StyledProfileWrapper>
            <ProfileName profile={profile} />
            <DotDivider />
            <div>{content.counts.episodeCount}화</div>
          </StyledProfileWrapper>
        </StyledContentMetaWrapper>

        <StyledIcon onClick={stopPropagation}>
          <ContentFavoriteAndEpisodeNotiButton
            key="favoriteAndEpisodeNoti"
            contentId={content.id}
            color={Color.Gray500}
            opacity={0.5}
            trackerExtraInfo={trackerExtraInfo}
          />
        </StyledIcon>
      </StyledContentBanner>
    </StyledContentBannerWrapper>
  );
};

const StyledNewContentCover = styled(ContentCover)`
  flex-shrink: 0;
`;

const StyledContentBannerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledContentBanner = styled(ContentBodyPaddingContainer)`
  height: 78px;
  padding: 8px;

  border-radius: 4px;
  border: 1px solid ${Color.Gray300};
  background-color: ${Color.White};

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const StyledContentMetaWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`;

const StyledProfileWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;

  color: ${Color.Gray600};
  ${Typography.XXXSmall};
  font-weight: ${FontWeight.Medium};
`;

const StyledContentMetaTitle = styled.div`
  color: ${Color.Black};
  ${Typography.XSmall};
  font-weight: ${FontWeight.Bold};
  ${getTextLineStyle(1)};
`;

const StyledIcon = styled.div`
  display: flex;
  margin-left: auto;
  flex-direction: row;
  align-items: center;
  gap: 18px;
`;
